import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, partnership, guest_suggestion
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });
  const navigate = useNavigate();

  const fetchMessages = async (page = 1) => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages?page=${page}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        showAlert('Session Expired', 'Your session has expired. Please log in again.', () => navigate('/admin/login'));
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch messages');

      const data = await response.json();
      setMessages(data.items || []);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(data.page || 1);
    } catch (error) {
      console.error('Error fetching messages:', error);
      showAlert('Error', 'Failed to load messages. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(currentPage);
  }, [currentPage]);

  const showAlert = (title, message, onConfirm = null) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm });
  };

  const showConfirm = (title, message, onConfirm) => {
    setDialog({ isOpen: true, type: 'confirm', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/${messageId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      // Update local state smoothly
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: newStatus } : m));
      
      // If modal is open for this message, update it too
      if (selectedMessage && selectedMessage.id === messageId) {
        setSelectedMessage(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showAlert('Error', 'Failed to update message status.');
    }
  };

  const handleDelete = (messageId) => {
    showConfirm(
      'Delete Message',
      'Are you sure you want to delete this message? This action cannot be undone.',
      () => executeDelete(messageId)
    );
  };

  const executeDelete = async (messageId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete message');

      showAlert('Success', 'Message has been deleted!');
      // Refresh page if we just deleted the last item on the current page
      if (messages.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
         fetchMessages(currentPage);
      }
      setSelectedMessage(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      showAlert('Error', 'Failed to delete message. Please try again.');
    }
  };

  const filteredMessages = messages.filter(msg => {
    const matchesSearch = msg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          msg.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || msg.type === filterType;
    return matchesSearch && matchesType;
  });

  // Render specific payload fields gracefully
  const renderPayload = (msg) => {
    if (!msg || !msg.payload) return null;
    let data = msg.payload;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch(e) {}
    }

    if (msg.type === 'partnership') {
      return (
        <div className="space-y-4">
          <div>
            <div className="text-sm text-soft-gray mb-1">Brand Name</div>
            <div className="text-warm-white font-medium">{data.brandName || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-soft-gray mb-1">Contact Person</div>
            <div className="text-warm-white">{data.contactPerson || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-soft-gray mb-1">Website</div>
            <div className="text-warm-white text-electric-yellow hover:underline">
              <a href={data.website?.startsWith('http') ? data.website : `https://${data.website}`} target="_blank" rel="noreferrer">
                {data.website || '-'}
              </a>
            </div>
          </div>
          <div>
            <div className="text-sm text-soft-gray mb-1">Budget Range</div>
            <div className="text-warm-white bg-slate-dark/50 inline-block px-3 py-1 rounded-md">{data.budgetRange || '-'}</div>
          </div>
          <div className="bg-slate-dark/30 p-4 rounded-xl">
            <div className="text-sm text-soft-gray mb-2">Why Sponsor?</div>
            <div className="text-warm-white leading-relaxed">{data.whySponsor || '-'}</div>
          </div>
          <div className="bg-slate-dark/30 p-4 rounded-xl">
            <div className="text-sm text-soft-gray mb-2">Collaboration Ideas</div>
            <div className="text-warm-white leading-relaxed">{data.collaborationIdeas || '-'}</div>
          </div>
        </div>
      );
    }

    if (msg.type === 'guest_suggestion') {
      return (
        <div className="space-y-4">
          <div>
            <div className="text-sm text-soft-gray mb-1">Guest Name</div>
            <div className="text-warm-white font-medium">{data.guestName || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-soft-gray mb-1">Guest Email</div>
            <div className="text-warm-white">{data.guestEmail || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-soft-gray mb-1">Social Links</div>
            <div className="text-warm-white text-electric-yellow word-break break-all">
              {data.socialLinks || '-'}
            </div>
          </div>
          <div className="bg-slate-dark/30 p-4 rounded-xl">
            <div className="text-sm text-soft-gray mb-2">Why Nominate? (The Story)</div>
            <div className="text-warm-white leading-relaxed whitespace-pre-line">{data.whyNominate || '-'}</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-soft-gray mb-1">Relationship</div>
              <div className="text-warm-white">{data.relationship || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-soft-gray mb-1">Nominated By</div>
              <div className="text-warm-white">{data.referrerName || 'Anonymous'}</div>
            </div>
          </div>
        </div>
      );
    }
    
    return <pre className="text-warm-white bg-slate-dark/50 p-4 rounded-xl overflow-x-auto text-sm">{JSON.stringify(data, null, 2)}</pre>;
  };

  const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="font-clash text-3xl font-bold text-electric-yellow">Inbox Messages</h2>
      </div>

      {/* Filters and Search */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-midnight/80 border border-slate-dark rounded-xl pl-12 pr-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
          />
          <svg 
            className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-soft-gray"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="w-full sm:w-auto bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 min-w-[200px]"
        >
          <option value="all">All Types</option>
          <option value="partnership">Partnership Applications</option>
          <option value="guest_suggestion">Guest Suggestions</option>
        </select>
      </div>

      {/* Messages Table */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-dark/50 text-soft-gray text-sm">
              <tr>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Sender / Subject</th>
                <th className="px-6 py-4 font-medium">Type</th>
                <th className="px-6 py-4 font-medium">Date Received</th>
                <th className="px-6 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-dark/30">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-soft-gray">
                    <div className="animate-pulse flex items-center justify-center gap-2">
                      <div className="w-2 h-2 bg-electric-yellow rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-electric-yellow rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-electric-yellow rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </td>
                </tr>
              ) : filteredMessages.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-soft-gray">
                    {searchTerm || filterType !== 'all' ? 'No messages found matching your filters.' : 'Your inbox is empty.'}
                  </td>
                </tr>
              ) : (
                filteredMessages.map((msg) => (
                  <tr 
                    key={msg.id} 
                    className={`transition-colors duration-200 cursor-pointer ${
                      msg.status === 'unread' ? 'bg-electric-yellow/5 hover:bg-electric-yellow/10' : 'hover:bg-slate-dark/20'
                    }`}
                    onClick={() => {
                      setSelectedMessage(msg);
                      if (msg.status === 'unread') {
                        handleStatusChange(msg.id, 'read');
                      }
                    }}
                  >
                    <td className="px-6 py-4">
                      {msg.status === 'unread' ? (
                        <span className="flex items-center gap-2 text-electric-yellow font-medium text-sm">
                          <span className="w-2 h-2 rounded-full bg-electric-yellow animate-pulse"></span>
                          New
                        </span>
                      ) : (
                        <span className="text-soft-gray font-medium text-sm">Read</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className={`font-medium ${msg.status === 'unread' ? 'text-electric-yellow' : 'text-warm-white'}`}>{msg.name}</div>
                      <div className="text-soft-gray text-sm">{msg.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                        msg.type === 'partnership' 
                          ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' 
                          : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                      }`}>
                        {msg.type === 'partnership' ? 'Partnership' : 'Guest Suggestion'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-soft-gray text-sm">
                      {new Date(msg.createdAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 text-right space-x-3" onClick={e => e.stopPropagation()}>
                       <button
                        onClick={() => {
                          setSelectedMessage(msg);
                          if (msg.status === 'unread') {
                            handleStatusChange(msg.id, 'read');
                          }
                        }}
                        className="text-soft-gray hover:text-deep-teal transition-colors p-2 rounded-lg hover:bg-deep-teal/10"
                        title="View message"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(msg.id)}
                        className="text-soft-gray hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-500/10"
                        title="Delete message"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-dark/30 flex items-center justify-between">
            <span className="text-soft-gray text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-midnight/60 rounded-lg text-sm font-medium text-warm-white hover:bg-slate-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-slate-dark/50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-midnight/60 rounded-lg text-sm font-medium text-warm-white hover:bg-slate-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-slate-dark/50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Message Modal View */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-midnight/95 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6">
          <div className="bg-charcoal/90 backdrop-blur-md border border-slate-dark/50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-slate-dark/30">
                <div className="w-full sm:w-auto">
                  <h3 className="font-clash text-xl sm:text-2xl font-bold text-electric-yellow mb-1 break-words">
                    {selectedMessage.type === 'partnership' ? 'Partnership Application' : 'Guest Suggestion'}
                  </h3>
                  <div className="text-soft-gray text-xs sm:text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                    <span>{new Date(selectedMessage.createdAt).toLocaleString()}</span>
                    <span className="hidden sm:inline">•</span>
                    <a href={`mailto:${selectedMessage.email}`} className="text-electric-yellow hover:underline break-all">{selectedMessage.email}</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 justify-between sm:justify-end w-full sm:w-auto mt-2 sm:mt-0">
                  <button
                    onClick={() => {
                        handleStatusChange(selectedMessage.id, selectedMessage.status === 'read' ? 'unread' : 'read');
                    }}
                    className="text-sm px-3 py-1.5 rounded-lg border border-slate-dark text-warm-white hover:bg-slate-dark transition-colors"
                  >
                    Mark as {selectedMessage.status === 'read' ? 'Unread' : 'Read'}
                  </button>
                  <button
                    onClick={() => setSelectedMessage(null)}
                    className="text-soft-gray hover:text-electric-yellow transition-colors duration-300 p-2"
                  >
                    <CloseIcon />
                  </button>
                </div>
              </div>

              {/* Dynamic Payload Rendering */}
              {renderPayload(selectedMessage)}

              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-dark/30 flex flex-col sm:flex-row justify-between gap-4">
                <button
                  onClick={() => {
                    handleDelete(selectedMessage.id);
                  }}
                  className="w-full sm:w-auto justify-center text-red-400 hover:text-red-300 transition-colors font-medium flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-red-500/10 border border-transparent sm:border-none hover:border-red-500/50"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Message
                </button>
                
                <a
                  href={`mailto:${selectedMessage.email}`}
                  className="w-full sm:w-auto text-center bg-electric-yellow text-midnight px-6 py-3 sm:py-2 rounded-xl font-bold hover:bg-electric-yellow/90 transition-all duration-300"
                >
                  Reply via Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Dialog Alert/Confirm Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-center gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Confirm' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
